import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981', warning: '#F59E0B',
  grey: '#6B7280', border: '#E5E7EB', bg: '#F8FAFC', white: '#FFFFFF',
};

export default function MyAssessment() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const viewResult = async () => {
    if (detail) { setDetailOpen(o => !o); return; }
    try {
      const r = await api.get('/api/assessment/result');
      setDetail(r.data);
      setDetailOpen(true);
    } catch { setDetailOpen(false); }
  };

  useEffect(() => {
    api.get('/api/student/assessment/my-bookings')
      .then(r => setData(r.data || { bookings: [], result: null }))
      .catch(() => setData({ bookings: [], result: null }))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (d, t) => {
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) + (t ? ` · ${t}` : ''); }
    catch { return d; }
  };

  const bookings = data?.bookings || [];
  const result = data?.result;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '32px 20px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h1 style={{ color: C.navy, fontSize: 26, fontWeight: 800, margin: '0 0 4px' }}>My Assessment</h1>
        <p style={{ color: C.grey, margin: '0 0 24px' }}>Your booked English level assessment and result.</p>

        {loading ? (
          <div style={{ textAlign: 'center', color: C.grey, padding: 40 }}>Loading…</div>
        ) : (
          <>
            {result && (
              <div style={{ background: '#ECFDF5', border: `1px solid ${C.success}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: C.navy }}>Assessment completed ✅</div>
                    <div style={{ color: C.grey, fontSize: 13 }}>Your level: <strong style={{ color: C.success }}>{result.cefr_level}</strong></div>
                  </div>
                  <button onClick={viewResult} style={{ padding: '9px 16px', borderRadius: 8, background: '#fff', color: C.success, border: `2px solid ${C.success}`, fontWeight: 700, cursor: 'pointer' }}>{detailOpen ? 'Hide result' : 'View my result'}</button>
                </div>
                {detailOpen && detail && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.success}` }}>
                    <div style={{ fontSize: 13, color: C.grey, marginBottom: 14 }}>
                      🚀 This assessment is your <strong>starting point</strong>. Here's where you are now — and the guided path we'll take you on from here.
                    </div>

                    {/* Level + score */}
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                      <div style={{ flex: '1 1 220px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                        <div style={{ fontSize: 11, color: C.grey, fontWeight: 700, letterSpacing: 0.5 }}>YOUR LEVEL</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: C.success }}>{detail.cefr_level} — {detail.level_name}</div>
                        {detail.level_description && <div style={{ fontSize: 13, color: C.grey, marginTop: 4 }}>{detail.level_description}</div>}
                      </div>
                      {typeof detail.overall_score === 'number' && (
                        <div style={{ flex: '0 0 110px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: C.grey, fontWeight: 700, letterSpacing: 0.5 }}>SCORE</div>
                          <div style={{ fontSize: 28, fontWeight: 800, color: C.navy }}>{Math.round(detail.overall_score)}%</div>
                        </div>
                      )}
                    </div>

                    {Array.isArray(detail.strengths) && detail.strengths.length > 0 && (
                      <div style={{ fontSize: 13, color: C.navy, marginBottom: 6 }}><strong>✅ Your strengths:</strong> {detail.strengths.map(s => (typeof s === 'object' && s !== null) ? (s.topic || s.area || s.skill || s.name || '') : s).filter(Boolean).join(', ')}</div>
                    )}
                    {Array.isArray(detail.weak_areas) && detail.weak_areas.length > 0 && (
                      <div style={{ fontSize: 13, color: C.navy, marginBottom: 16 }}><strong>📈 We'll focus on:</strong> {detail.weak_areas.map(s => (typeof s === 'object' && s !== null) ? (s.topic || s.area || s.skill || s.name || '') : s).filter(Boolean).join(', ')}</div>
                    )}

                    {/* Recommended programme + benefits */}
                    {detail.recommended_package?.primary && (
                      <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                        <div style={{ fontSize: 11, color: '#1D4ED8', fontWeight: 700, letterSpacing: 0.5, marginBottom: 2 }}>RECOMMENDED FOR YOU</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: C.navy }}>{detail.recommended_package.primary}</div>
                        {detail.recommended_package.reason && <div style={{ fontSize: 13, color: C.navy, marginTop: 6 }}>{detail.recommended_package.reason}</div>}
                        <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13, color: C.navy, lineHeight: 1.7 }}>
                          <li>Learn with a qualified tutor in small group classes</li>
                          <li>Structured lessons that target your weak areas above</li>
                          <li>Steady, guided progress through every level</li>
                        </ul>
                      </div>
                    )}

                    {/* Roadmap */}
                    {Array.isArray(detail.roadmap) && detail.roadmap.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 8 }}>🗺️ Your roadmap to fluency</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {detail.roadmap.map((seg, i) => (
                            <div key={seg.level} style={{ display: 'flex', alignItems: 'center', gap: 12, background: i === 0 ? '#ECFDF5' : '#fff', border: `1px solid ${i === 0 ? C.success : C.border}`, borderRadius: 10, padding: '10px 14px' }}>
                              <div style={{ width: 40, height: 40, borderRadius: 8, background: i === 0 ? C.success : C.navy, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{seg.level}</div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{seg.level} → {seg.next_level}{i === 0 ? '  · you are here' : ''}</div>
                                <div style={{ fontSize: 12, color: C.grey }}>{seg.sessions} sessions · ~{seg.weeks} weeks</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize: 12, color: C.grey, marginTop: 8 }}>Complete each level to unlock the next. Your school guides you the whole way.</div>
                      </div>
                    )}

                    <button onClick={() => navigate('/programmes?recommended=' + encodeURIComponent(detail.recommended_package?.primary || ''))} style={{ width: '100%', padding: '13px', borderRadius: 10, background: C.teal, color: '#fff', border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
                      Enrol in {detail.recommended_package?.primary || 'classes'} &amp; start your journey →
                    </button>
                  </div>
                )}
              </div>
            )}

            {bookings.length === 0 && !result ? (
              <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 48, textAlign: 'center', color: C.grey }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
                You have no assessment booked yet. Once your booking is confirmed by the centre, it will appear here.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {bookings.map(b => (
                  <div key={b.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: C.navy, fontSize: 16 }}>Free English Assessment</div>
                      <div style={{ color: C.grey, fontSize: 13, marginTop: 4 }}>
                        {fmt(b.date, b.time)}{b.location ? ` · 📍 ${b.location}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {result ? (
                        <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: '#D1FAE5', color: C.success }}>Completed</span>
                      ) : b.attended ? (
                        <button onClick={() => navigate('/assessment')} style={{ padding: '9px 18px', borderRadius: 8, background: C.teal, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>Start Assessment →</button>
                      ) : (
                        <span style={{ fontSize: 13, color: C.warning, fontWeight: 600 }}>Booked · staff will check you in on the day</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!result && (
              <div style={{ marginTop: 18, fontSize: 13, color: C.grey }}>
                On the day: bring photo ID, arrive a few minutes early, and a staff member will check you in. The assessment takes about 15–20 minutes.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
